/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { IntlShape } from 'react-intl';
import { ICheckboxGroupOption } from '../Forms/Fields/CheckboxGroup';
import { IMultipleSelectOption } from '../Forms/Fields/MultipleSelect';
import FormikValidatorService, {
  IValidationApiResponse,
  IValidationRule,
} from '../../Services/FormikValidatorService';
import { IOption } from '../Forms/CmdField';
import { DATE_SPAN, DAYS_MONTHS, DAYS_OF_WEEK, TIME_SPAN } from './constants';
import { getRequest } from '../../Services/HttpService';

export const getTimeFrameValues = async (): Promise<IOption[] | false> => {
  const response = await getRequest<IOption[] | false>('/v1/alarm/form/timeFrames');
  return response as IOption[] | false;
};

export const getMonthsValues = async (): Promise<IOption[] | false> => {
  const response = await getRequest<IOption[] | false>('/v1/alarm/time-frame/form/daysMonths.months');
  return response as IOption[] | false;
};

export const getTimeFrameValidation = async (): Promise<IValidationApiResponse | false> => {
  const response = await getRequest('/v1/alarm/time-frame/validation');
  return response as IValidationApiResponse | false;
};

export const setTimeFrameValues = async (
  dispatchSetState: React.Dispatch<React.SetStateAction<IOption[]>>
): Promise<void> => {
  const values: IOption[] | false = await getTimeFrameValues();
  if (values) {
    dispatchSetState(values);
  }
};

export const setMonthsValues = async (
  dispatchSetState: React.Dispatch<React.SetStateAction<IMultipleSelectOption[]>>
): Promise<void> => {
  const values: ICheckboxGroupOption[] | false = await getMonthsValues();
  if (values) {
    dispatchSetState(values);
  }
};

export const getDayInWeekValues = async (): Promise<ICheckboxGroupOption[] | false> => {
  const response = await getRequest('/v1/alarm/time-frame/form/daysOfWeek');
  return response as ICheckboxGroupOption[] | false;
};

export const setDaysOfWeekValues = async (
  dispatchSetState: React.Dispatch<React.SetStateAction<ICheckboxGroupOption[]>>
): Promise<void> => {
  const values: ICheckboxGroupOption[] | false = await getDayInWeekValues();
  if (values) {
    dispatchSetState(values);
  }
};

export const setTimeFrameValidation = async (
  dispatchSetState: React.Dispatch<React.SetStateAction<IValidationApiResponse | null>>,
  formatMessage: any
): Promise<void> => {
  const response = await getTimeFrameValidation();
  if (response) {
    const minTime = response.timeSpan.find((rule: IValidationRule) => rule.param === 'minTime');
    const maxTime = response.timeSpan.find((rule: IValidationRule) => rule.param === 'maxTime');
    const required = response.timeSpan.find((rule: IValidationRule) => rule.param === 'required');
    const timeFromValidation: IValidationRule[] = [];
    const timeToValidation: IValidationRule[] = [];
    if (minTime) {
      timeFromValidation.push(minTime);
    }
    if (required) {
      timeFromValidation.push(required);
      timeToValidation.push(required);
    }
    if (maxTime) {
      timeToValidation.push(maxTime);
    }

    const timeFrameValidation: IValidationApiResponse = {
      ...response,
      timeFrame: [
        {
          param: 'required',
          value: true,
          message: formatMessage({ id: 'validationRule.required' }),
        },
      ],
      timeFrom: timeFromValidation,
      timeTo: timeToValidation,
    };

    timeFrameValidation.days = response['daysMonths.days'].map((item: IValidationRule) => {
      if (item.param === 'requiredAlt') {
        const newItem = {
          ...item,
          value: item.value.map((ruleValue: string) => {
            return ruleValue.replace('daysMonths.', '');
          }),
        };
        return newItem;
      }
      return item;
    });
    timeFrameValidation.months = response['daysMonths.months'].map((item: IValidationRule) => {
      if (item.param === 'requiredAlt') {
        const newItem = {
          ...item,
          value: item.value.map((ruleValue: string) => {
            return ruleValue.replace('daysMonths.', '');
          }),
        };
        return newItem;
      }
      return item;
    });

    dispatchSetState(timeFrameValidation);
  }
};

export const createDays = (numberOfDays = 31): IMultipleSelectOption[] => {
  const days = Array.from(Array(numberOfDays).keys()).map((day: number) => {
    const item: IMultipleSelectOption = {
      value: day + 1,
      item: String(day + 1),
    };
    return item;
  });
  return days;
};

export const prepareTimeFrameDataForEdit = (editingData: any, timeFrameData: any): any => {
  if (!editingData && timeFrameData.length === 0) {
    return [];
  }
  if (!editingData && timeFrameData.length > 0) {
    return timeFrameData;
  }
  if (
    (editingData.length === 0 && timeFrameData.length > 0) ||
    (editingData.length > 0 && timeFrameData.length > 0)
  ) {
    return timeFrameData;
  }
  return editingData;
};
export const validate = (
  values: Record<any, any>,
  validation: IValidationApiResponse | null,
  intlShape?: IntlShape
): Record<any, any> => {
  const errors: Record<any, any> = { timeFrames: [] };
  if (!validation) {
    return errors;
  }
  values.timeFrames.map((timeFrame: Record<any, any>, index: number) => {
    if (!timeFrame.timeFrame) {
      const newTimeFrame = {
        timeFrame: timeFrame.timeFrame,
      };
      const errorMessage = FormikValidatorService.validateByValidationApiResponse(
        newTimeFrame,
        validation
      );
      if (Array.isArray(errors.timeFrames)) {
        if (Object.keys(errorMessage).length > 0) {
          errors.timeFrames.splice(index, 0, errorMessage);
        } else {
          errors.timeFrames.splice(index, 0, {});
        }
      }
    }
    if (timeFrame.timeFrame === DATE_SPAN) {
      const newTimeFrame = {
        timeFrame: timeFrame.timeFrame,
        dateSpan: timeFrame.dateSpan,
      };
      const errorMessage = FormikValidatorService.validateByValidationApiResponse(
        newTimeFrame,
        validation
      );
      if (Array.isArray(errors.timeFrames)) {
        if (Object.keys(errorMessage).length > 0) {
          errors.timeFrames.splice(index, 0, errorMessage);
        } else {
          errors.timeFrames.splice(index, 0, {});
        }
      }
    }
    if (timeFrame.timeFrame === TIME_SPAN) {
      const newTimeFrame = {
        timeFrame: timeFrame.timeFrame,
        timeFrom: timeFrame.timeFrom,
        timeTo: timeFrame.timeTo,
      };
      const errorMessage = FormikValidatorService.validateByValidationApiResponse(
        newTimeFrame,
        validation
      );
      if (!FormikValidatorService.validateTimeFrom(timeFrame.timeFrom, timeFrame.timeTo)) {
        if (errorMessage) {
          if (intlShape) {
            const { formatMessage: f } = intlShape;
            errorMessage.timeFrom = f({
              id: 'alarms.modal.timeFrame.timeFrom.validation.error.timeFromIsGreaterThanTimeTo',
            });
          }
        }
      }
      if (Array.isArray(errors.timeFrames)) {
        if (Object.keys(errorMessage).length > 0) {
          errors.timeFrames.splice(index, 0, errorMessage);
        } else {
          errors.timeFrames.splice(index, 0, {});
        }
      }
    }
    if (timeFrame.timeFrame === DAYS_OF_WEEK) {
      const newTimeFrame = {
        timeFrame: timeFrame.timeFrame,
        daysOfWeek: timeFrame.daysOfWeek,
      };
      const errorMessage = FormikValidatorService.validateByValidationApiResponse(
        newTimeFrame,
        validation
      );
      if (Array.isArray(errors.timeFrames)) {
        if (Object.keys(errorMessage).length > 0) {
          errors.timeFrames.splice(index, 0, errorMessage);
        } else {
          errors.timeFrames.splice(index, 0, {});
        }
      }
    }
    if (timeFrame.timeFrame === DAYS_MONTHS) {
      const newTimeFrame = {
        timeFrame: timeFrame.timeFrame,
        days: timeFrame.days,
        months: timeFrame.months,
      };
      const errorMessage = FormikValidatorService.validateByValidationApiResponse(
        newTimeFrame,
        validation
      );
      if (Array.isArray(errors.timeFrames)) {
        if (Object.keys(errorMessage).length > 0) {
          errors.timeFrames.splice(index, 0, errorMessage);
        } else {
          errors.timeFrames.splice(index, 0, {});
        }
      }
    }
  });
  if (errors.timeFrames) {
    if (errors.timeFrames.length === 0) {
      return {};
    }
    // A bizarre handling of error object for nested array errors
    // There is always an empty object in array when no error occurs
    // So we check for non empty objects
    let isEmpty = true;
    if (Array.isArray(errors.timeFrames) && errors.timeFrames.length > 0) {
      (errors.timeFrames as Record<string, unknown>[]).map((item: any, index: number) => {
        if (typeof item === 'object' && Object.keys(item).length > 0) {
          isEmpty = false;
        }
      });
    }
    if (isEmpty) {
      return {};
    }
  }
  console.info('Timeframe validation errors: ', errors);
  return errors;
};
